import React, { ReactNode, createContext, useContext, useEffect, useState } from 'react'

interface NavigationContextType {
  isUSUser: boolean
  setIsUSUser: (isUS: boolean) => void
  clearNavigationState: () => void
}

const NavigationContext = createContext<NavigationContextType | undefined>(undefined)

const NAVIGATION_SESSION_KEY = 'isUSUser'

export const NavigationProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  // Keep server + first client render consistent to avoid hydration mismatch warnings.
  const [isUSUser, setIsUSUserState] = useState<boolean>(false)

  useEffect(() => {
    // Client-only persistence.
    if (typeof window === 'undefined') return
    try {
      const stored = window.sessionStorage?.getItem(NAVIGATION_SESSION_KEY)
      if (stored === 'true') setIsUSUserState(true)
      else setIsUSUserState(false)
    } catch {
      // If storage is blocked/unavailable, fall back to default false.
      setIsUSUserState(false)
    }
  }, [])

  const setIsUSUser = (isUS: boolean) => {
    setIsUSUserState(isUS)
    if (typeof window === 'undefined') return
    try {
      window.sessionStorage?.setItem(NAVIGATION_SESSION_KEY, String(isUS))
    } catch {
      // ignore storage errors
    }
  }

  const clearNavigationState = () => {
    setIsUSUser(false)
    if (typeof window === 'undefined') return
    try {
      window.sessionStorage?.removeItem(NAVIGATION_SESSION_KEY)
    } catch {
      // ignore storage errors
    }
  }

  return (
    <NavigationContext.Provider value={{ isUSUser, setIsUSUser, clearNavigationState }}>
      {children}
    </NavigationContext.Provider>
  )
}

export const useNavigationContext = () => {
  const context = useContext(NavigationContext)
  if (context === undefined) {
    // Safe fallback so the app never crashes if provider wiring changes.
    return {
      isUSUser: false,
      setIsUSUser: () => undefined,
      clearNavigationState: () => undefined,
    }
  }
  return context
}
